import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { BOOKING_URL, IMAGES } from "@/constants/site";

const fadeUp = {
  hidden: { opacity: 0, y: 36 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } },
};

const FACTS = [
  { k: "8 years", v: "of hands-on detailing experience" },
  { k: "4 years", v: "managing high-end vehicles at car dealerships" },
  { k: "1 standard", v: "dealership-level results, every single vehicle" },
];

export const About = () => (
  <section id="about" data-testid="about-section" className="relative py-24 md:py-36">
    <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-5 md:grid-cols-12 md:gap-10 md:px-10 lg:gap-16">
      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.15 }}
        className="relative md:col-span-5"
      >
        <div className="absolute -left-3 -top-3 h-full w-full border border-[#E60000]/50" aria-hidden="true" />
        <div className="relative overflow-hidden">
          <img
            src={IMAGES.about}
            alt="Marquel Dean hand-washing a customer's car"
            loading="lazy"
            className="h-[420px] w-full object-cover md:h-[560px]"
          />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_35%,transparent_0%,rgba(5,5,5,0.55)_100%)]" />
          <div className="absolute bottom-0 left-0 bg-[#E60000] px-5 py-2.5 font-display text-xs font-semibold uppercase tracking-[0.25em] text-white">
            Marquel Dean — Owner
          </div>
        </div>
      </motion.div>

      <div className="flex flex-col justify-center md:col-span-7 lg:pl-8">
        <motion.p
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          className="mb-4 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.3em] text-[#E60000]"
        >
          <span className="h-px w-10 bg-[#E60000]" />
          The person behind the shine
        </motion.p>

        <motion.h2
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          className="font-display text-4xl font-bold uppercase leading-[0.95] tracking-tight sm:text-5xl md:text-6xl"
        >
          Built on trust.
          <br />
          <span className="text-outline">Proven on paint.</span>
        </motion.h2>

        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          className="mt-8 space-y-5 text-base leading-relaxed text-zinc-300 md:text-lg"
        >
          <p>
            MARR Mobile Detailing is owned and operated by{" "}
            <span className="font-semibold text-white">Marquel Dean</span> — eight years of
            hands-on detailing, including four years managing high-end vehicles at car
            dealerships, where perfection wasn't a perk, it was the job.
          </p>
          <p>
            Alongside dealership work, Marquel detailed independently and built something more
            valuable than a client list: long-term relationships earned through consistent
            quality and reliability — never one-off transactions.
          </p>
          <p>
            MARR was founded to bring that same{" "}
            <span className="font-semibold text-white">dealership-level standard</span>{" "}
            directly to your driveway, with the convenience of a fully mobile service. You
            don't come to us. <span className="text-[#E60000] font-semibold">We come to you.</span>
          </p>
        </motion.div>

        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          className="mt-10 grid grid-cols-1 gap-px border border-white/10 bg-white/10 sm:grid-cols-3"
        >
          {FACTS.map((f) => (
            <div key={f.k} className="bg-[#0A0A0A] p-5">
              <div className="font-display text-2xl font-bold uppercase text-white">{f.k}</div>
              <div className="mt-1 text-xs leading-relaxed text-zinc-400">{f.v}</div>
            </div>
          ))}
        </motion.div>

        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          className="mt-10"
        >
          <a
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="about-book-now-button"
            className="group inline-flex items-center gap-2 font-display text-sm font-semibold uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:text-[#E60000]"
          >
            Book your detail
            <span className="h-px w-10 bg-[#E60000] transition-[width] duration-300 group-hover:w-16" />
            <ArrowUpRight size={16} />
          </a>
        </motion.div>
      </div>
    </div>
  </section>
);

export default About;
